import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('emr_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('emr_token');
      localStorage.removeItem('emr_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Patients API
export const patientsAPI = {
  getAll: (params) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
  getTimeline: (id) => api.get(`/patients/${id}/timeline`),
  getAttachments: (id) => api.get(`/patients/${id}/attachments`),
  getSummary: (id) => api.get(`/patients/${id}/summary`),
};

// Forms API
export const formsAPI = {
  // Templates
  getTemplates: (params) => api.get('/forms/templates', { params }),
  getTemplateById: (id) => api.get(`/forms/templates/${id}`),
  createTemplate: (data) => api.post('/forms/templates', data),
  updateTemplate: (id, data) => api.put(`/forms/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/forms/templates/${id}`),
  
  // Submissions
  getSubmissions: (params) => api.get('/forms/submissions', { params }),
  getSubmissionById: (id) => api.get(`/forms/submissions/${id}`),
  createSubmission: (data) => api.post('/forms/submissions', data),
};

// Clinical Notes API
export const clinicalNotesAPI = {
  transcribe: (audioFile) => {
    const formData = new FormData();
    formData.append('audio', audioFile);
    return api.post('/clinical-notes/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  generate: (data) => api.post('/clinical-notes/generate', data),
  approve: (noteId, data) => api.post(`/clinical-notes/${noteId}/approve`, data),
  getAll: (params) => api.get('/clinical-notes', { params }),
  getById: (id) => api.get(`/clinical-notes/${id}`),
};

// Invoices API
export const invoicesAPI = {
  create: (data) => api.post('/invoices', data),
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  updateStatus: (id, status) => api.put(`/invoices/${id}/status?status=${status}`),
};

// Event Quotes API
export const eventQuotesAPI = {
  submit: (data) => api.post('/event-quotes', data),
  getAll: (params) => api.get('/event-quotes', { params }),
  updateStatus: (id, status) => api.put(`/event-quotes/${id}/status?status=${status}`),
};

// Files API
export const filesAPI = {
  upload: (patientId, file, fileType = 'document') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/files/upload?patient_id=${patientId}&file_type=${fileType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (fileId) => api.get(`/files/${fileId}`, { responseType: 'blob' }),
};

// Audit Logs API
export const auditAPI = {
  getLogs: (params) => api.get('/audit-logs', { params }),
};

// Export API
export const exportAPI = {
  patients: (format = 'csv') => api.get(`/export/patients?format=${format}`, { responseType: 'blob' }),
  submissions: (params) => api.get('/export/submissions', { params, responseType: 'blob' }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Config API
export const configAPI = {
  getClinic: () => api.get('/config/clinic'),
};

// Google Sheets Integration API (Mock)
export const integrationsAPI = {
  configureGoogleSheets: (data) => api.post('/integrations/google-sheets/configure', data),
  getGoogleSheetsConfigs: () => api.get('/integrations/google-sheets/configs'),
  syncGoogleSheets: (configId) => api.post(`/integrations/google-sheets/sync/${configId}`),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Appointments API
export const appointmentsAPI = {
  // CRUD operations
  create: (data) => api.post('/appointments', data),
  getAll: (params) => api.get('/appointments', { params }),
  getById: (id) => api.get(`/appointments/${id}`),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  updateStatus: (id, status) => api.put(`/appointments/${id}/status?status=${status}`),
  delete: (id) => api.delete(`/appointments/${id}`),
  
  // Calendar and agenda views
  getCalendarEvents: (startDate, endDate, providerId = null) => {
    const params = { start_date: startDate, end_date: endDate };
    if (providerId) params.provider_id = providerId;
    return api.get('/appointments/calendar', { params });
  },
  getAgenda: (date, providerId = null) => {
    const params = { date };
    if (providerId) params.provider_id = providerId;
    return api.get('/appointments/agenda', { params });
  },
  
  // Stats
  getStats: (dateFrom, dateTo) => api.get('/appointments/stats', { params: { date_from: dateFrom, date_to: dateTo } }),
  
  // Reminders
  getPendingReminders: (limit = 100) => api.get('/appointments/reminders/pending', { params: { limit } }),
  getReminderLog: (params) => api.get('/appointments/reminders/log', { params }),
  sendReminderNow: (appointmentId, type = 'both') => api.post(`/appointments/${appointmentId}/reminders/send?reminder_type=${type}`),
  
  // Communication preferences
  getPatientPreferences: (patientId) => api.get(`/appointments/patients/${patientId}/communication-preferences`),
  updatePatientPreferences: (patientId, data) => api.put(`/appointments/patients/${patientId}/communication-preferences`, data),
  optOut: (patientId, channel) => api.post(`/appointments/patients/${patientId}/opt-out/${channel}`),
  optIn: (patientId, channel) => api.post(`/appointments/patients/${patientId}/opt-in/${channel}`),
  
  // Communication logs
  getCommunicationLogs: (params) => api.get('/appointments/communication-logs', { params }),
  
  // Metadata
  getTypes: () => api.get('/appointments/types'),
  getReminderTimings: () => api.get('/appointments/reminder-timings'),
};

export default api;
